import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  TextField,
  Stack,
  Divider,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';

import { Link as RouterLink, useLocation } from 'react-router-dom';
import Logo from '../logo.png';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openQuote, setOpenQuote] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  /* ================= SCROLL EFFECT ================= */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Products', path: '/products' },
    { label: 'Clients', path: '/clients' },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /* ================= MOBILE DRAWER ================= */
  const drawer = (
    <Box sx={{ width: 280, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={Logo} sx={{ height: 36 }} />
          <Typography fontWeight="bold">Simiga Consummate</Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              borderRadius: 2,
              mb: 1,
              backgroundColor: location.pathname === item.path ? 'primary.main' : 'transparent',
              color: location.pathname === item.path ? 'white' : 'text.primary',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* ================= HEADER ================= */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          transition: 'all 0.4s ease',
          background: scrolled ? 'rgba(255,255,255,0.78)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : 'none',
          boxShadow: scrolled ? '0 8px 30px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>

            {/* LOGO */}
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none' }}
            >
              <Box component="img" src={Logo} alt="Logo" sx={{ height: 42 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: scrolled ? '#0F172A' : '#FFFFFF',
                }}
              >
                Simiga Consummate
              </Typography>
            </Box>

            {/* DESKTOP MENU */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>

                {navItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Button
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: scrolled ? '#334155' : '#FFFFFF',
                        fontWeight: active ? 600 : 500,
                        position: 'relative',

                        '&::after': active
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 4,
                              left: '20%',
                              width: '60%',
                              height: 2,
                              backgroundColor: '#2563EB',
                              borderRadius: 2,
                            }
                          : {},

                        '&:hover': {
                          color: '#2563EB',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}

                {/* DIAMOND CTA BUTTON */}
                <Button
                  onClick={() => setOpenQuote(true)}
                  sx={{
                    ml: 2,
                    px: 3.5,
                    py: 1.2,
                    borderRadius: 999,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#EAF4FF',
                    position: 'relative',
                    overflow: 'hidden',
                    background: `
                      linear-gradient(135deg,
                        #0b1c3d 0%,
                        #1e3a8a 20%,
                        #2563eb 40%,
                        #38bdf8 55%,
                        #1d4ed8 70%,
                        #0b1c3d 100%)
                    `,
                    backgroundSize: '300% 300%',
                    animation: 'diamondMove 6s ease infinite',
                    boxShadow: `
                      0 8px 30px rgba(37,99,235,0.45),
                      inset 0 0 12px rgba(147,197,253,0.55)
                    `,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-120%',
                      width: '120%',
                      height: '100%',
                      background:
                        'linear-gradient(120deg, transparent, rgba(255,255,255,0.6), transparent)',
                      animation: 'shine 5s infinite',
                    },
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.03)',
                      boxShadow: `
                        0 16px 45px rgba(56,189,248,0.75),
                        inset 0 0 20px rgba(147,197,253,0.75)
                      `,
                    },
                  }}
                >
                  Get Quote
                </Button>

              </Box>
            ) : (
              <IconButton onClick={handleDrawerToggle} sx={{ color: scrolled ? '#0F172A' : '#FFFFFF' }}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {drawer}
      </Drawer>

      {/* ================= GLASS CONTACT POPUP ================= */}
      <Dialog
        open={openQuote}
        onClose={() => setOpenQuote(false)}
        fullWidth
        maxWidth="xs"
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(0,0,0,0.35)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '22px',
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 80px rgba(0,0,0,0.25)',
            p: 3,
          },
        }}
      >
        <DialogContent>

          <Typography
            variant="h6"
            sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#0F172A' }}
          >
            Get in Touch with Simiga Consummate
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Contact Info */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EmailIcon color="primary" />
              <Typography fontWeight={600}>simigaconsummate@gmail.com</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneIcon color="primary" />
              <Typography fontWeight={600}>+91 74494 33633</Typography>
            </Box>
          </Stack>

          {/* Mini Form */}
          <Stack spacing={2}>
            <TextField label="Your Name" fullWidth size="small" />
            <TextField label="Your Email" fullWidth size="small" />
            <TextField label="Message" multiline rows={3} fullWidth size="small" />

            <Button
              startIcon={<SendIcon />}
              component="a"
              href={`mailto:simigaconsummate@gmail.com?subject=Business Enquiry`}
              sx={{
                mt: 1,
                py: 1.3,
                borderRadius: '999px',
                fontWeight: 700,
                textTransform: 'none',
                color: '#fff',
                background: 'linear-gradient(135deg,#2563EB,#4F46E5)',
                boxShadow: '0 8px 30px rgba(37,99,235,0.45)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#1D4ED8,#4338CA)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Send Enquiry Mail
            </Button>

            <Button
              onClick={() => setOpenQuote(false)}
              sx={{
                py: 1,
                borderRadius: '999px',
                fontWeight: 600,
                textTransform: 'none',
                color: '#475569',
              }}
            >
              Close
            </Button>
          </Stack>

        </DialogContent>
      </Dialog>

      {/* ================= ANIMATIONS ================= */}
      <style>{`
        @keyframes diamondMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes shine {
          0% { left: -120%; }
          40% { left: 120%; }
          100% { left: 120%; }
        }
      `}</style>
    </>
  );
};

export default Header;
